/**
 * Panel de Validación de Pagos para Asociación
 * Muestra todos los pagos subidos por los clubes para un evento,
 * agrupados por club, con opciones de aprobar/rechazar.
 */

import { useState, useMemo } from "react";
import { Button, Badge } from "@/components/ui";
import {
  X,
  Loader2,
  Search,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Building2,
  ChevronDown,
  ChevronRight,
  FileText,
  AlertCircle,
  Pencil,
  Banknote,
} from "lucide-react";
import { useEventPayments, useValidatePayment } from "@/hooks/useEvents";
import { useDecryptedUrl } from "@/hooks/useDecryptedUrl";
import { parseAmountCOP } from "@/lib/money";
import type { EncryptionMeta } from "@/lib/services/crypto";
import type { Event, EventPayment, EventPaymentStatus } from "@/types";
import { Can } from "@/components/auth/Can";

interface PaymentValidationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
}

const statusLabels: Record<EventPaymentStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
};

const statusColors: Record<EventPaymentStatus, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const statusIcons: Record<EventPaymentStatus, React.ReactNode> = {
  pending: <Clock className="w-3.5 h-3.5" />,
  approved: <CheckCircle className="w-3.5 h-3.5" />,
  rejected: <XCircle className="w-3.5 h-3.5" />,
};

// Helper para timestamp
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  return new Date();
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(amount);
}

// Sub-componente para ver comprobante de un pago individual
function PaymentReceiptViewer({ payment }: { payment: EventPayment }) {
  const [showReceipt, setShowReceipt] = useState(false);
  const { decryptedUrl, isDecrypting, error } = useDecryptedUrl({
    url: showReceipt ? payment.receiptUrl : null,
    encryption: (payment.receiptEncryption as EncryptionMeta) || null,
    contentType: payment.receiptContentType,
  });

  // Pago en efectivo: no hay comprobante.
  if (payment.paymentMethod === "cash" || !payment.receiptUrl) {
    return (
      <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">
        <Banknote className="w-3.5 h-3.5" />
        Pago en efectivo · sin comprobante
      </p>
    );
  }

  if (!showReceipt) {
    return (
      <button
        onClick={() => setShowReceipt(true)}
        className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-700 font-medium"
      >
        <Eye className="w-3.5 h-3.5" />
        Ver comprobante
      </button>
    );
  }

  return (
    <div className="mt-2 border-2 border-gray-100 rounded-xl overflow-hidden">
      {isDecrypting ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
          <span className="ml-2 text-sm text-gray-500">Desencriptando...</span>
        </div>
      ) : error ? (
        <div className="p-4 text-sm text-red-600 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      ) : decryptedUrl ? (
        <div className="relative">
          {payment.receiptContentType?.startsWith("image/") ? (
            <img
              src={decryptedUrl}
              alt="Comprobante"
              className="w-full max-h-80 object-contain bg-gray-50"
            />
          ) : (
            <a
              href={decryptedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-4 text-purple-600 hover:text-purple-700"
            >
              <FileText className="w-5 h-5" />
              Abrir comprobante
            </a>
          )}
          <button
            onClick={() => setShowReceipt(false)}
            className="absolute top-2 right-2 bg-white/90 p-1 rounded-full shadow-sm hover:bg-white"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function PaymentValidationPanel({
  isOpen,
  onClose,
  event,
}: PaymentValidationPanelProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<EventPaymentStatus | "all">(
    "all",
  );
  const [expandedClubs, setExpandedClubs] = useState<Set<string>>(new Set());
  const [rejectingPayment, setRejectingPayment] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  // Edición de monto (admin asociación corrige montos mal capturados por el club)
  const [editingAmountFor, setEditingAmountFor] = useState<string | null>(null);
  const [editAmountValue, setEditAmountValue] = useState<string>("");

  const { data: payments, isLoading } = useEventPayments(event.id);
  const validateMutation = useValidatePayment();

  // Agrupar pagos por club
  const paymentsByClub = useMemo(() => {
    if (!payments) return [];

    const filtered =
      statusFilter === "all"
        ? payments
        : payments.filter((p) => p.status === statusFilter);

    const clubMap = new Map<
      string,
      { clubId: string; clubName: string; payments: EventPayment[] }
    >();

    filtered.forEach((p) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !p.clubName.toLowerCase().includes(term) &&
          !p.description?.toLowerCase().includes(term)
        ) {
          return;
        }
      }

      if (!clubMap.has(p.clubId)) {
        clubMap.set(p.clubId, {
          clubId: p.clubId,
          clubName: p.clubName,
          payments: [],
        });
      }
      clubMap.get(p.clubId)!.payments.push(p);
    });

    return Array.from(clubMap.values()).sort((a, b) =>
      a.clubName.localeCompare(b.clubName),
    );
  }, [payments, statusFilter, searchTerm]);

  // Estadísticas globales
  const stats = useMemo(() => {
    if (!payments)
      return {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        totalAmount: 0,
        approvedAmount: 0,
      };
    return {
      total: payments.length,
      pending: payments.filter((p) => p.status === "pending").length,
      approved: payments.filter((p) => p.status === "approved").length,
      rejected: payments.filter((p) => p.status === "rejected").length,
      totalAmount: payments.reduce((sum, p) => sum + p.amount, 0),
      approvedAmount: payments
        .filter((p) => p.status === "approved")
        .reduce((sum, p) => sum + p.amount, 0),
    };
  }, [payments]);

  const toggleClub = (clubId: string) => {
    setExpandedClubs((prev) => {
      const next = new Set(prev);
      if (next.has(clubId)) next.delete(clubId);
      else next.add(clubId);
      return next;
    });
  };

  const handleApprove = async (payment: EventPayment) => {
    // Mantenido para compatibilidad: ahora delega a handleSaveAndApprove
    // que maneja el caso con/sin edición de monto.
    return handleSaveAndApprove(payment);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void handleApprove;

  const startEditAmount = (payment: EventPayment) => {
    setEditingAmountFor(payment.id);
    setEditAmountValue(String(payment.amount));
  };

  const cancelEditAmount = () => {
    setEditingAmountFor(null);
    setEditAmountValue("");
  };

  // El parser vive en src/lib/money.ts: el modal de subida tenía el suyo
  // propio (parseFloat) y por eso "1.350.000" se guardaba como $1,35.
  const parseEditedAmount = (): number | null => parseAmountCOP(editAmountValue);

  const handleSaveAndApprove = async (payment: EventPayment) => {
    // Solo interpretar el campo de edición si ESTE pago está en modo edición.
    // Sin este guard, editAmountValue="" se parsea como 0 (Number("") === 0) y
    // el botón "Aprobar" normal termina aprobando el pago con monto $0.
    const isEditingThis = editingAmountFor === payment.id;
    const newAmount = isEditingThis ? parseEditedAmount() : payment.amount;
    if (newAmount === null) {
      window.alert("El monto ingresado no es válido. Revisa el valor.");
      return;
    }
    const changed = newAmount !== payment.amount;
    if (changed) {
      const ok = window.confirm(
        `Vas a corregir el monto del pago de ${payment.clubName}:\n\n` +
          `  Monto declarado por el club:  ${formatCurrency(payment.amount)}\n` +
          `  Monto corregido por ti:       ${formatCurrency(newAmount)}\n\n` +
          `Quedará registrado quién hizo el ajuste y desde qué valor.\n\n¿Aprobar con el monto corregido?`,
      );
      if (!ok) return;
    }
    await validateMutation.mutateAsync({
      paymentId: payment.id,
      status: "approved",
      eventId: event.id,
      newAmount: changed ? newAmount : undefined,
    });
    cancelEditAmount();
  };

  const handleReject = async (paymentId: string) => {
    await validateMutation.mutateAsync({
      paymentId,
      status: "rejected",
      eventId: event.id,
      rejectionReason: rejectionReason.trim() || undefined,
    });
    setRejectingPayment(null);
    setRejectionReason("");
  };

  const handleRevert = async (payment: EventPayment) => {
    const verb = payment.status === "approved" ? "aprobación" : "rechazo";
    const ok = window.confirm(
      `¿Revertir la ${verb} del pago de ${formatCurrency(payment.amount)} de ${payment.clubName}?\n\nEl pago volverá a estado "Pendiente" y podrás revisarlo de nuevo.`,
    );
    if (!ok) return;
    await validateMutation.mutateAsync({
      paymentId: payment.id,
      status: "pending",
      eventId: event.id,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-8">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <DollarSign className="w-6 h-6" />
                Validar Pagos
              </h2>
              <p className="text-purple-200 text-sm mt-1">{event.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs text-purple-200">Total</p>
              <p className="text-lg font-bold">{stats.total}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs text-yellow-200">Pendientes</p>
              <p className="text-lg font-bold text-yellow-300">
                {stats.pending}
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs text-green-200">Aprobados</p>
              <p className="text-lg font-bold text-green-300">
                {stats.approved}
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xs text-purple-200">Monto Aprobado</p>
              <p className="text-lg font-bold">
                {formatCurrency(stats.approvedAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 flex-shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar club..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
            />
          </div>
          <div className="flex gap-2">
            {(["all", "pending", "approved", "rejected"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-2 text-sm rounded-xl border transition-colors ${
                  statusFilter === s
                    ? "bg-purple-100 border-purple-300 text-purple-700 font-medium"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {s === "all" ? "Todos" : statusLabels[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
            </div>
          ) : paymentsByClub.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-lg font-medium">No hay pagos</p>
              <p className="text-gray-400 text-sm">
                {statusFilter !== "all"
                  ? `No hay pagos con estado "${statusLabels[statusFilter]}"`
                  : "Los clubes aún no han subido pagos para este evento"}
              </p>
            </div>
          ) : (
            paymentsByClub.map((club) => {
              const isExpanded = expandedClubs.has(club.clubId);
              const clubTotal = club.payments.reduce((s, p) => s + p.amount, 0);
              const clubPending = club.payments.filter(
                (p) => p.status === "pending",
              ).length;

              return (
                <div
                  key={club.clubId}
                  className="border-2 border-gray-100 rounded-xl overflow-hidden"
                >
                  {/* Club header */}
                  <button
                    onClick={() => toggleClub(club.clubId)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-gray-400" />
                      <div className="text-left">
                        <p className="font-semibold text-gray-900">
                          {club.clubName}
                        </p>
                        <p className="text-sm text-gray-500">
                          {club.payments.length} pago
                          {club.payments.length !== 1 ? "s" : ""} ·{" "}
                          {formatCurrency(clubTotal)}
                        </p>
                      </div>
                      {clubPending > 0 && (
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                          {clubPending} pendiente{clubPending !== 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {/* Club payments */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 divide-y divide-gray-50">
                      {club.payments.map((payment) => (
                        <div key={payment.id} className="p-4 bg-gray-50/50">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {editingAmountFor === payment.id ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">
                                      $
                                    </span>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={editAmountValue}
                                      onChange={(e) =>
                                        setEditAmountValue(e.target.value)
                                      }
                                      autoFocus
                                      className="w-36 font-bold text-lg border-2 border-purple-300 rounded-lg px-2 py-1 focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500"
                                      placeholder="0"
                                    />
                                    <button
                                      onClick={cancelEditAmount}
                                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                                      type="button"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="font-bold text-lg">
                                      {formatCurrency(payment.amount)}
                                    </span>
                                    {payment.status === "pending" && (
                                      <button
                                        onClick={() => startEditAmount(payment)}
                                        className="text-purple-600 hover:text-purple-700 p-1 rounded-md hover:bg-purple-50"
                                        title="Corregir monto antes de aprobar"
                                        type="button"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </>
                                )}
                                <Badge
                                  className={`${statusColors[payment.status]} text-xs flex items-center gap-1`}
                                >
                                  {statusIcons[payment.status]}
                                  {statusLabels[payment.status]}
                                </Badge>
                              </div>
                              {typeof payment.originalAmount === "number" &&
                                payment.originalAmount !== payment.amount && (
                                  <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-md inline-flex items-center gap-1 mb-1">
                                    <Pencil className="w-3 h-3" />
                                    Monto corregido. Original declarado por el
                                    club:{" "}
                                    {formatCurrency(payment.originalAmount)}
                                    {payment.amountAdjustedByName &&
                                      ` · ajustado por ${payment.amountAdjustedByName}`}
                                  </p>
                                )}
                              {payment.description && (
                                <p className="text-sm text-gray-600 mb-1">
                                  {payment.description}
                                </p>
                              )}
                              <p className="text-xs text-gray-400">
                                Subido por {payment.uploadedByName} ·{" "}
                                {toSafeDate(
                                  payment.uploadedAt,
                                ).toLocaleDateString("es-CO", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                              {payment.validatedByName && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {payment.status === "approved"
                                    ? "Aprobado"
                                    : "Rechazado"}{" "}
                                  por {payment.validatedByName}
                                  {payment.validatedAt &&
                                    ` · ${toSafeDate(payment.validatedAt).toLocaleDateString("es-CO", { day: "numeric", month: "short" })}`}
                                </p>
                              )}
                              {payment.rejectionReason && (
                                <p className="text-xs text-red-600 mt-1 bg-red-50 p-2 rounded-lg">
                                  <strong>Motivo:</strong>{" "}
                                  {payment.rejectionReason}
                                </p>
                              )}
                              {payment.notes && (
                                <p className="text-xs text-gray-500 mt-1 italic">
                                  Nota: {payment.notes}
                                </p>
                              )}

                              {/* Comprobante viewer */}
                              <PaymentReceiptViewer payment={payment} />
                            </div>

                            {/* Actions */}
                            {payment.status === "pending" && (
                              <Can permission="finance.approve">
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                  <Button
                                    size="sm"
                                    onClick={() =>
                                      handleSaveAndApprove(payment)
                                    }
                                    disabled={validateMutation.isPending}
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                    title={
                                      editingAmountFor === payment.id
                                        ? "Aprobar con el monto editado"
                                        : "Aprobar el pago"
                                    }
                                  >
                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                    {editingAmountFor === payment.id
                                      ? "Guardar y aprobar"
                                      : "Aprobar"}
                                  </Button>
                                  {rejectingPayment === payment.id ? (
                                    <div className="space-y-2">
                                      <textarea
                                        value={rejectionReason}
                                        onChange={(e) =>
                                          setRejectionReason(e.target.value)
                                        }
                                        placeholder="Motivo del rechazo..."
                                        className="w-full text-xs border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                                        rows={2}
                                      />
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          onClick={() =>
                                            handleReject(payment.id)
                                          }
                                          disabled={validateMutation.isPending}
                                          className="bg-red-600 hover:bg-red-700 text-white text-xs flex-1"
                                        >
                                          Confirmar
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setRejectingPayment(null);
                                            setRejectionReason("");
                                          }}
                                          className="text-xs"
                                        >
                                          Cancelar
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        setRejectingPayment(payment.id)
                                      }
                                      className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
                                    >
                                      <XCircle className="w-3.5 h-3.5 mr-1" />
                                      Rechazar
                                    </Button>
                                  )}
                                </div>
                              </Can>
                            )}
                            {payment.status !== "pending" && (
                              <Can permission="finance.approve">
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRevert(payment)}
                                    disabled={validateMutation.isPending}
                                    className="text-amber-700 border-amber-300 hover:bg-amber-50 text-xs"
                                    title="Devolver el pago a estado pendiente para revisarlo nuevamente"
                                  >
                                    <Clock className="w-3.5 h-3.5 mr-1" />
                                    Revertir
                                  </Button>
                                </div>
                              </Can>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
